import { TFunction } from 'i18next';
import type { DriveStep } from 'driver.js';

import { getParticipantRegistrationState } from 'src/helpers/participantList';
import { canRegisterParticipant } from 'src/helpers/userCheckinWizard';
import { IEnrichedParticipant } from 'src/interfaces/Participant';

export const MY_PARTICIPANTS_TOUR_REGISTER_SELECTOR = '[data-tour="my-participants-register"]';
export const MY_PARTICIPANTS_TOUR_QR_SELECTOR = '[data-tour="my-participants-qr"]';

export function getMyParticipantsTourRegisterAnchorId(
  participants: IEnrichedParticipant[],
  options: { isActiveEditionView: boolean; hasRegisterHandler: boolean },
): string | undefined {
  if (!options.isActiveEditionView || !options.hasRegisterHandler) {
    return undefined;
  }

  const participant = participants.find(
    p => getParticipantRegistrationState(p) === 'unregistered' && canRegisterParticipant(p),
  );

  return participant?.id;
}

export function getMyParticipantsTourQrAnchorId(
  participants: IEnrichedParticipant[],
): string | undefined {
  const participant = participants.find(p => {
    const state = getParticipantRegistrationState(p);
    return (state === 'registered' || state === 'checked_in') && !!p.registration?.id;
  });

  return participant?.id;
}

export function buildMyParticipantsTourSteps(
  t: TFunction,
  options: { showRegisterStep: boolean; showQrStep: boolean },
): DriveStep[] {
  const listStep = options.showRegisterStep
    ? {
        element: '[data-tour="my-participants-list"]',
        popover: {
          title: t('participants.tour.steps.listWithRegister.title'),
          description: t('participants.tour.steps.listWithRegister.description'),
        },
      }
    : {
        element: '[data-tour="my-participants-list"]',
        popover: {
          title: t('participants.tour.steps.list.title'),
          description: t('participants.tour.steps.list.description'),
        },
      };

  const steps: DriveStep[] = [
    {
      popover: {
        title: t('participants.tour.steps.welcome.title'),
        description: t('participants.tour.steps.welcome.description'),
      },
    },
    {
      element: '[data-tour="my-participants-edition"]',
      popover: {
        title: t('participants.tour.steps.edition.title'),
        description: t('participants.tour.steps.edition.description'),
      },
    },
    {
      element: '[data-tour="my-participants-add"]',
      popover: {
        title: t('participants.tour.steps.add.title'),
        description: t('participants.tour.steps.add.description'),
      },
    },
    {
      element: '[data-tour="my-participants-claim"]',
      popover: {
        title: t('participants.tour.steps.claim.title'),
        description: t('participants.tour.steps.claim.description'),
      },
    },
    listStep,
  ];

  if (options.showRegisterStep) {
    steps.push({
      element: MY_PARTICIPANTS_TOUR_REGISTER_SELECTOR,
      popover: {
        title: t('participants.tour.steps.register.title'),
        description: t('participants.tour.steps.register.description'),
      },
    });
  }

  if (options.showQrStep) {
    steps.push({
      element: MY_PARTICIPANTS_TOUR_QR_SELECTOR,
      popover: {
        title: t('participants.tour.steps.qr.title'),
        description: t('participants.tour.steps.qr.description'),
      },
    });
  }

  steps.push({
    popover: {
      title: t('participants.tour.steps.done.title'),
      description: t('participants.tour.steps.done.description'),
    },
  });

  return steps;
}
